import { useNavigate } from '@tanstack/react-router'
import clsx from 'clsx'
import type { SubscriptionTier } from '../queries'
import styles from './SubscriptionCard.module.css'

type SubscriptionCardProps = {
  tier:        SubscriptionTier
  leagueCount: number
}

const TIER_CONFIG = {
  BETA: {
    icon:      '⚡',
    name:      'Beta',
    badge:     'Beta',
    leagueMax: 10,
    sports:    ['FOOTBALL'] as const,
  },
  FREE: {
    icon:      '🆓',
    name:      'Gratuito',
    badge:     'Free',
    leagueMax: 1,
    sports:    ['FOOTBALL'] as const,
  },
  ORGANIZADOR: {
    icon:      '⭐',
    name:      'Organizador',
    badge:     'Org',
    leagueMax: 5,
    sports:    ['FOOTBALL'] as const,
  },
  LIGA_PRO: {
    icon:      '👑',
    name:      'Liga Pro',
    badge:     'Pro',
    leagueMax: 5,
    sports:    ['FOOTBALL'] as const,
  },
} as const satisfies Record<SubscriptionTier, { icon: string; name: string; badge: string; leagueMax: number; sports: readonly string[] }>

const ALL_SPORTS = [
  { key: 'FOOTBALL', icon: '⚽', label: 'Futebol' },
  { key: 'CS2',      icon: '🎮', label: 'CS2' },
  { key: 'BASKETBALL', icon: '🏀', label: 'Basketball' },
] as const

export function SubscriptionCard({ tier, leagueCount }: SubscriptionCardProps) {
  const navigate = useNavigate()
  const config   = TIER_CONFIG[tier] ?? TIER_CONFIG.FREE
  const isBeta    = tier === 'BETA'
  const showUpsell = tier !== 'LIGA_PRO' && !isBeta
  const leagueMax  = config.leagueMax
  const usageWidth = Math.min(100, (leagueCount / config.leagueMax) * 100)

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.current}>
          <span className={styles.icon}>{config.icon}</span>
          <div>
            <div className={styles.planLabel}>Plano atual</div>
            <div className={styles.planName}>{config.name}</div>
          </div>
        </div>
        <span className={styles.tierBadge}>{config.badge}</span>
      </div>

      <div className={styles.body}>
        <div className={styles.sectionLabel}>Esportes incluídos</div>
        <div className={styles.sportsRow}>
          {ALL_SPORTS.map(sport => {
            const isActive = (config.sports as readonly string[]).includes(sport.key)
            return (
              <span
                key={sport.key}
                className={clsx(styles.chip, isActive ? styles.chipActive : styles.chipLocked)}
              >
                {sport.icon} {sport.label}
                {!isActive && <span className={styles.chipLock}>🔒</span>}
              </span>
            )
          })}
        </div>

        <div className={styles.limitHeader}>
          <span className={styles.limitLabel}>Ligas utilizadas</span>
          <span className={styles.limitValue}>{leagueCount} / {leagueMax}</span>
        </div>
        <div className={styles.bar}>
          <div
            className={styles.barFill}
            style={{ width: `${usageWidth}%` }}
          />
        </div>

        {isBeta && (
          <div className={styles.upsell}>
            <div className={styles.upsellTitle}>Acesso completo no beta ⚡</div>
            <ul className={styles.upsellList}>
              <li>Até 10 ligas fantasy</li>
              <li>Até 10 jogos de sobrevivente</li>
              <li>Todos os esportes desbloqueados</li>
            </ul>
          </div>
        )}

        {showUpsell && (
          <div className={styles.upsell}>
            <div className={styles.upsellTitle}>Liga Pro — R$49/mês</div>
            <ul className={styles.upsellList}>
              <li>Ligas ilimitadas</li>
              <li>Todos os esportes desbloqueados</li>
              <li>Draft mode para ligas privadas</li>
              <li>Painel analytics avançado</li>
            </ul>
          </div>
        )}

        {showUpsell && (
          <button
            type="button"
            className={styles.btnUpgrade}
            onClick={() => void navigate({ to: '/subscription' })}
          >
            🚀 Upgrade para Liga Pro
          </button>
        )}

        {!isBeta && (
          <button
            type="button"
            className={styles.btnPlans}
            onClick={() => void navigate({ to: '/subscription' })}
          >
            Ver todos os planos
          </button>
        )}
      </div>
    </div>
  )
}
